import { Injectable, OnDestroy, Inject } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { auditTime, catchError, map, takeUntil, tap } from 'rxjs/operators';
import { FeatureFlagsApiService } from './api/feature-flags-api.service';
import { FeatureToggler, FeatureValue } from './feature-toggler';
import { FeatureFlagContextService } from './client/feature-flag-context.service';
import { FeatureFlagClientService } from './client/feature-flag-client.service';
import { EvaluationContext, FeatureFlag } from './models/feature-flag.model';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

/**
 * Implementation of FeatureToggler that uses a remote API to load feature flags
 * but evaluates them client-side for better performance and offline capability
 */
@Injectable()
export class RemoteFeatureTogglerService implements FeatureToggler, OnDestroy {
  private _state: Record<string, FeatureValue> = {};
  #featuresState = new Subject<Record<string, boolean | FeatureValue>>();
  #loaded = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<void>();

  /**
   * Observable that emits when the feature flags have been loaded
   */
  readonly loaded$ = this.#loaded.asObservable();

  /**
   * Observable that emits when feature flags state is changed
   */
  readonly featureStateChanged$ = this.#featuresState.pipe(
    auditTime(10),
    map(() => undefined)
  );

  constructor(
    private apiService: FeatureFlagsApiService,
    private contextService: FeatureFlagContextService,
    private featureFlagClient: FeatureFlagClientService,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {
    // Subscribe to context changes to re-evaluate feature flags
    this.contextService.context$.pipe(takeUntil(this.destroy$)).subscribe(() => this.evaluateAllFeatureFlags());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load feature flags from the API
   */
  loadFeatureFlags(): Observable<void> {
    return this.apiService.getAllFeatureFlags().pipe(
      tap(featureFlags => {
        // Initialize the client-side evaluator with the fetched flags
        this.featureFlagClient.initialize(featureFlags);

        // Convert API response to feature state for compatibility with existing code
        const featureState: Record<string, FeatureValue> = {};
        featureFlags.forEach(flag => {
          // For boolean flags, we can set them directly
          if (flag.rule.type === 'boolean') {
            featureState[flag.id] = (flag.rule as any).value;
          } else {
            // For complex rules, store a reference object to indicate it needs evaluation
            featureState[flag.id] = { value: false };
          }
        });

        this._state = featureState;
        this.#featuresState.next(featureState);
        this.#loaded.next(true);

        // Evaluate all feature flags with current context
        this.evaluateAllFeatureFlags();
      }),
      catchError(error => {
        this.logger.error(`Error loading feature flags: ${error?.message ?? error}`);
        this.#loaded.next(true); // Mark as loaded even on error
        return of(undefined);
      }),
      map(() => undefined)
    );
  }

  /**
   * Check if a feature is enabled
   * @param featureName The feature flag ID
   * @returns Boolean indicating if the feature is enabled
   */
  isFeatureEnabled(featureName: string): boolean {
    const feature = this._state[featureName];

    // If we don't have this feature in state yet (possible race condition)
    if (feature == null) {
      // Evaluate directly using the client service
      return this.featureFlagClient.isEnabledWithDefault(featureName, this.contextService.getCurrentContext(), false);
    }

    // If it's a boolean value, return it directly
    if (typeof feature === 'boolean') {
      return feature;
    }

    // For complex rules, always delegate to the client service for evaluation
    return this.featureFlagClient.isEnabled(featureName, this.contextService.getCurrentContext());
  }

  /**
   * Set a feature's value manually
   * @param featureName The feature flag ID
   * @param value The value to set for the feature
   */
  setFeature(featureName: string, value: boolean | FeatureValue): void {
    // Create a temporary feature flag to use with the client service
    const tempFlag: FeatureFlag = {
      id: featureName,
      name: featureName,
      description: 'Manually set feature flag',
      status: 'active',
      rule: {
        type: 'boolean',
        value: typeof value === 'boolean' ? value : false,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'manual',
      updatedBy: 'manual',
    };

    // Update the client service
    this.featureFlagClient.setFlag(tempFlag);

    // Update our local state
    const nextState = { ...this._state, [featureName]: value };
    this.setState(nextState);
  }

  setState(state: Record<string, FeatureValue>) {
    this._state = state;
    this.#featuresState.next(state);
  }

  /**
   * Evaluate all feature flags that require evaluation with the current context
   */
  private evaluateAllFeatureFlags(): void {
    const currentContext = this.contextService.getCurrentContext();
    const nextState = { ...this._state };

    // Only evaluate flags that aren't simple boolean values
    for (const featureId of Object.keys(this._state)) {
      const result = this.featureFlagClient.isEnabled(featureId, currentContext);
      nextState[featureId] = result;
    }

    this.setState(nextState);
  }

  /**
   * Identify the user context for feature flag evaluation
   * @param context The user context
   */
  identify(context: EvaluationContext | string): void {
    if (typeof context === 'string') {
      context = { email: context };
    }

    this.contextService.patchContext(context);
  }

  get state(): Record<string, boolean | FeatureValue> {
    return this._state;
  }
}
